import {
  CountrySwitch,
  CountrySwitchContext,
  DeliveryLocationNotification,
  HeaderBar,
  IconLinksBar,
  NavigationBar,
  ThemeVariables,
} from '@cms/ui';
import { getFeatureFlagValue as getFeatureFlagValueAbby } from '@hub/libs/abby/abby';
import { memo, useContext, useMemo } from 'react';
import { useCartArticleCount } from './hooks/useCartArticleCount';
import { useRouter } from 'next/router';
import { getLocale } from '@hub/libs/constants';
import { getLocaleWithFallbackRegion } from '@hub/libs/i18n.helper';
import Locale from '@formatjs/intl-locale';
import { useSessionStore } from '@hub/hooks/states/useSessionStore';
import type { NextRuntimeConfiguration } from '@hub/libs/config';

import '@cms/ui/src/styling/igus-icons.css';
import '@cms/ui/src/styling/theme.css';
import '@cms/ui/src/styling/header-cms-ui.css';
import '@cms/ui/dist/index.css';

import getConfig from 'next/config';
import { getLowercaseLocale } from './libs/header.helper';
import type { LinkData } from '@cms/ui/dist/src/components/molecules/countrySwitch/CountrySwitch.types';
import type { LanguageAlternate } from '@hub/models/data';
import type { HeaderProps } from './types';

export const Header = memo(({ header, translations, default_form_slug, languageAlternates }: HeaderProps) => {
  const cartArticleCount = useCartArticleCount();
  const router = useRouter();
  const { locale } = router;
  const lowercaseLocale = getLowercaseLocale(locale);
  const { region } = useMemo(() => new Locale(getLocaleWithFallbackRegion(getLocale({ locale }))), [locale]);
  const session = useSessionStore(state => state.session);
  const { setIsModalOpen } = useContext(CountrySwitchContext);

  // If there is no session, the countryFromSession would be undefined. This would cause the DeliveryLocationNotification to always be rendered on the first visit because undefined is treated the same as a different country.
  // To avoid this, we set the countryFromSession to the region obtained from the locale, which is derived from the next router so that locale and countryFromSession are the "same" on the first visit until the session is available.
  const countryFromSession = session ? session.userCountry : region;

  const showLanguageSwitcher = getFeatureFlagValueAbby('useNewHeaderLanguageSwitcher');

  const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

  const languageAlternatesForCountrySwitch: LinkData[] = languageAlternates
    .filter((la: LanguageAlternate) => la.hrefLang !== 'x-default')
    .map((la: LanguageAlternate) => ({
      full_slug: la.href,
      locale: la.hrefLang,
    }));

  return (
    <>
      <ThemeVariables />
      <section id="country-switch" />
      <header>
        <DeliveryLocationNotification
          headline={translations.DELIVERY_LOCATION_HEADLINE}
          subtext={translations.DELIVERY_LOCATION_SUBTEXT}
          buttonViewAllLocation={translations.VIEW_ALL_LOCATIONS}
          buttonGoToLocaleDomain={translations.GO_TO_LOCALE_DOMAIN_LINK}
          locale={lowercaseLocale}
          countryFromSession={countryFromSession.toLocaleLowerCase() || 'de'}
          openCountrySwitchModal={setIsModalOpen}
          alternateLinksForCountry={languageAlternatesForCountrySwitch}
        />
        {header?.links && header.links.length > 0 && (
          <IconLinksBar links={header.links} background_color={header.background_color} />
        )}
        <HeaderBar
          searchBarPlaceholder={translations.HEADER_NAV_SEARCH}
          shoppingCartName={translations.HEADER_NAV_SHOPPINGCART}
          shoppingCartValue={cartArticleCount}
          with_country_switch={showLanguageSwitcher}
          openCountrySwitchModal={setIsModalOpen}
          country={countryFromSession.toLocaleLowerCase() || 'de'}
          myIgusDomain="/myigus"
          router={router}
          profileText={translations.MY_IGUS_LOGIN}
        />
        {header?.navigation && !!header.navigation.length && (
          <NavigationBar
            with_contact={header.with_contact}
            defaultFormSlug={default_form_slug}
            country={lowercaseLocale}
            select_contact_view={header.select_contact_view}
            contact_link={header.contact_link}
            navigation={header.navigation}
            openCountrySwitchModal={setIsModalOpen}
            with_country_switch={showLanguageSwitcher}
            myContactLabel={translations.MY_CONTACT_LINK_LABEL}
            selectLocationText={translations.SELECT_LOCATION}
            selectPlaceholderText={translations.SELECT_PLACEHOLDER}
            noContactFoundText={translations.CONTACT_PERSON_NO_CONTACTS_FOUND}
            changeLocationText={translations.CHANGE_LOCATION}
            sendMessageText={translations.CONTACT_PERSON_SEND_MESSAGE}
            locationDescriptionText={translations.LOCATION_DESCRIPTION}
            zipCodePlaceholderText={translations.ZIP_CODE_PLACEHOLDER}
            locationErrorText={translations.LOCATION_ERROR}
            findOutMoreText={translations.FIND_OUT_MORE}
            overViewLinkText={translations.HEADER_OVERVIEW}
            middlewareBaseURL={publicRuntimeConfig.header.apiUrl}
          />
        )}
      </header>
      <CountrySwitch
        alternateLinksForCountrySwitch={languageAlternatesForCountrySwitch}
        countryOrRegionText={translations.COUNTRY_OR_REGION}
        notificationText={translations.COUNTRY_SELECT_NOTIFICATION_TEXT}
        languageSettingsHeadline={translations.COUNTRY_SELECT_LANGUAGE_SETTINGS_HEADLINE}
        requestLanguageChangeText={translations.COUNTRY_SELECT_REQUEST_LANG_CHANGE_TEXT}
        linkTitle={translations.COUNTRY_SELECT_LINK_TITLE}
        unavailableLinksText={translations.COUNTRY_SELECT_UNAVAILABLE_LINKS_TEXT}
        unavailablePageText={translations.COUNTRY_SELECT_UNAVAILABLE_PAGE_TEXT}
        internationalLinksText={translations.COUNTRY_SELECT_AVAILABLE_INTERNATIONAL_LINKS}
        switchToCountryHomepageText={translations.COUNTRY_SELECT_SWITCH_COUNTRY_TEXT}
        cancelButtonText={translations.COUNTRY_SELECT_CANCEL_BUTTON_TEXT}
        okayButtonText={translations.COUNTRY_SELECT_OKAY_BUTTON_TEXT}
        inputPlaceholderText={translations.COUNTRY_SELECT_INPUT_PLACEHOLDER_TEXT}
        internationalLinkTitleEn={translations.COUNTRY_SELECT_INTERNATIONAL_LINK_TITLE_EN}
        internationalLinkTextEn={translations.COUNTRY_SELECT_INTERNATIONAL_LINK_TEXT_EN}
        internationalLinkTitleEs={translations.COUNTRY_SELECT_INTERNATIONAL_LINK_TITLE_ES}
        internationalLinkTextEs={translations.COUNTRY_SELECT_INTERNATIONAL_LINK_TITLE_ES}
        homepageText={translations.COUNTRY_SELECT_HOMEPAGE_TITLE}
        locale={lowercaseLocale}
        urlPath={router.asPath}
      />
    </>
  );
});
